/**
 * AttachmentsStep — pay app editor step 4 (supporting documents).
 */

import { useRef, useState, type DragEvent } from 'react'
import { uploadPayAppAttachment, deletePayAppAttachment } from '../../../api/attachments'

interface Attachment {
  id: number
  filename: string
  original_name: string | null
  file_size?: number | null
  change_order_id?: number | null
}

interface ChangeOrder {
  id: number
  co_number: number
  description?: string
}

interface Props {
  payAppId: number
  projectId: number
  attachments: Attachment[]
  changeOrders?: ChangeOrder[]
  onRefresh: () => void
}

const ACCEPT = '.pdf,.doc,.docx,.xls,.xlsx,.csv'

function extBadge(name: string | null | undefined): string {
  const ext = (name ?? '').split('.').pop()?.toUpperCase().slice(0, 3) || '?'
  return ext
}

function fileKey(name: string, size: number): string {
  return `${name}\0${size}`
}

function attLabel(att: Attachment): string {
  return att.original_name ?? att.filename
}

function AttachmentRow({
  att,
  onDelete,
}: {
  att: Attachment
  onDelete: (id: number) => void
}) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '10px 12px',
        marginBottom: 8,
        border: '1px solid var(--line)',
        borderRadius: 6,
        background: 'var(--bg2)',
      }}
      data-testid={`attachment-row-${att.id}`}
    >
      <span
        style={{
          fontSize: 9,
          fontWeight: 700,
          color: 'var(--ink3)',
          background: 'var(--line)',
          padding: '4px 6px',
          borderRadius: 4,
          flexShrink: 0,
          minWidth: 28,
          textAlign: 'center',
        }}
      >
        {extBadge(att.original_name ?? att.filename)}
      </span>
      <span
        style={{
          flex: 1,
          fontSize: 13,
          color: 'var(--ink)',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {attLabel(att)}
      </span>
      {att.file_size != null && att.file_size > 0 && (
        <span style={{ fontSize: 11, color: 'var(--ink4)', flexShrink: 0 }}>
          {Math.round(att.file_size / 1024)} KB
        </span>
      )}
      <button
        type="button"
        onClick={() => onDelete(att.id)}
        title="Remove attachment"
        style={{
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          fontSize: 14,
          color: 'var(--red)',
          flexShrink: 0,
          padding: 4,
        }}
      >
        🗑
      </button>
    </div>
  )
}

export default function AttachmentsStep({
  payAppId,
  attachments,
  onRefresh,
}: Props) {
  const fileRef = useRef<HTMLInputElement>(null)
  const [isUploading, setIsUploading] = useState(false)
  const [isDragOver, setIsDragOver] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const payAppAtts = attachments.filter((a) => !a.change_order_id)
  const coFileCount = attachments.filter((a) => a.change_order_id).length

  function isDuplicate(file: File): boolean {
    const key = fileKey(file.name, file.size)
    return payAppAtts.some((a) =>
      fileKey(attLabel(a), Number(a.file_size || 0)) === key,
    )
  }

  async function handleFiles(files: FileList | null) {
    if (!files || files.length === 0) return
    setIsUploading(true)
    setErr(null)
    try {
      for (const file of Array.from(files)) {
        if (isDuplicate(file)) {
          setErr(`"${file.name}" is already attached to this pay app`)
          continue
        }
        await uploadPayAppAttachment(payAppId, file)
      }
      onRefresh()
      if (fileRef.current) fileRef.current.value = ''
    } catch (ex) {
      setErr(ex instanceof Error ? ex.message : 'Upload failed')
    } finally {
      setIsUploading(false)
    }
  }

  function handleDragOver(e: DragEvent) {
    e.preventDefault()
    setIsDragOver(true)
  }

  function handleDragLeave() {
    setIsDragOver(false)
  }

  function handleDrop(e: DragEvent) {
    e.preventDefault()
    setIsDragOver(false)
    if (!isUploading) handleFiles(e.dataTransfer.files)
  }

  async function handleDelete(id: number) {
    if (!confirm('Remove this attachment?')) return
    setErr(null)
    try {
      await deletePayAppAttachment(id)
      onRefresh()
    } catch (ex) {
      setErr(ex instanceof Error ? ex.message : 'Delete failed')
    }
  }

  return (
    <div className="panel" style={{ padding: 14 }} data-testid="attachments-step">
      <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--ink)', marginBottom: 12 }}>
        Supporting Documents
      </div>

      {coFileCount > 0 && (
        <p
          style={{ fontSize: 12, color: 'var(--ink4)', margin: '0 0 12px' }}
          data-testid="attachments-co-hint"
        >
          {`Change order files are attached in step 2 (${coFileCount} file${coFileCount === 1 ? '' : 's'}).`}
        </p>
      )}

      <div
        role="button"
        tabIndex={0}
        onClick={() => !isUploading && fileRef.current?.click()}
        onKeyDown={e => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            if (!isUploading) fileRef.current?.click()
          }
        }}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        style={{
          border: `2px dashed ${isDragOver ? 'var(--blue)' : 'var(--line)'}`,
          borderRadius: 8,
          padding: '28px 16px',
          textAlign: 'center',
          cursor: isUploading ? 'wait' : 'pointer',
          marginBottom: 14,
          background: isDragOver ? 'var(--bg2)' : 'transparent',
          transition: 'border-color 0.15s, background 0.15s',
        }}
      >
        <input
          ref={fileRef}
          type="file"
          multiple
          accept={ACCEPT}
          disabled={isUploading}
          onChange={e => handleFiles(e.target.files)}
          style={{ display: 'none' }}
        />
        {isUploading ? (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
            <span
              style={{
                width: 18,
                height: 18,
                border: '2px solid var(--blue)',
                borderTopColor: 'transparent',
                borderRadius: '50%',
                display: 'inline-block',
                animation: 'attachments-spin 0.7s linear infinite',
              }}
            />
            <span style={{ fontSize: 13, color: 'var(--ink3)' }}>Uploading...</span>
          </div>
        ) : (
          <>
            <p style={{ fontSize: 13, color: 'var(--ink2)', margin: '0 0 6px' }}>
              Drop files here, or click to attach
            </p>
            <p style={{ fontSize: 11, color: 'var(--ink4)', margin: 0 }}>
              PDF, Word, Excel supported · Max 25MB per file
            </p>
          </>
        )}
      </div>

      {payAppAtts.length === 0 ? (
        <p style={{ fontSize: 13, color: 'var(--ink4)' }}>No attachments yet.</p>
      ) : (
        <div data-testid="attachments-group-pay-app">
          {payAppAtts.map((att) => (
            <AttachmentRow key={att.id} att={att} onDelete={handleDelete} />
          ))}
        </div>
      )}

      {err && <p style={{ color: 'var(--red)', fontSize: 12, marginTop: 10 }}>{err}</p>}

      <style>{`
        @keyframes attachments-spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  )
}
